import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
	Button,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import { JobStaffList } from "features/jobs";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { useRouter } from "utils";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	summaryTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
	},
	countMuted: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(0.5),
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: 0,
		display: "block",
		overflow: "hidden",
	},
	addRow: {
		display: "flex",
		justifyContent: "flex-start",
		padding: theme.spacing(1.5, 2, 0, 2),
	},
	addButton: {
		color: theme.palette.text.secondary,
		fontWeight: 600,
		"&:hover": {
			color: theme.palette.brand,
		},
	},
	addIcon: {
		marginRight: theme.spacing(1),
	},
}));

const readPagedTotal = (payload) => {
	if (payload == null) {
		return null;
	}
	const raw = payload.totalNumberOfRecords ?? payload.TotalNumberOfRecords;
	if (raw === undefined || raw === null) {
		return null;
	}
	const n = Number(raw);
	return Number.isFinite(n) ? n : null;
};

const fetchStaffJobTotal = (organisationId, staffId) =>
	safetyWiseApi
		.post(`${organisationId}/job/paged`, {
			page: 1,
			pageSize: 1,
			orderBy: "createdAt",
			ascending: false,
			searchKey: "",
			columnName: "staffId",
			columnValue: staffId,
		})
		.then((res) => readPagedTotal(res.data));

const StaffPhotosNotesPanel = ({
	person,
	organisationId,
	expanded,
	onExpandedChange,
	onAddPhotoNote,
}) => {
	const classes = useStyles();
	const { match } = useRouter();
	const [notesCount, setNotesCount] = useState(null);
	const staffIdFromRoute = match?.params?.staffId;
	const staffId = person?.id || staffIdFromRoute;
	const currentOrganisationFromStore = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);
	const organisationIdResolved = useMemo(() => {
		if (organisationId) {
			return organisationId;
		}
		const co = currentOrganisationFromStore;
		if (typeof co === "string") {
			return co;
		}
		if (co && co.id) {
			return co.id;
		}
		return "";
	}, [organisationId, currentOrganisationFromStore]);

	useEffect(() => {
		let cancelled = false;
		if (!organisationIdResolved || !staffId) {
			setNotesCount(null);
			return undefined;
		}
		fetchStaffJobTotal(organisationIdResolved, staffId)
			.then((total) => {
				if (!cancelled) {
					setNotesCount(total);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setNotesCount(null);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [organisationIdResolved, staffId]);

	useEffect(() => {
		if (!expanded || !organisationIdResolved || !staffId) {
			return undefined;
		}
		let cancelled = false;
		fetchStaffJobTotal(organisationIdResolved, staffId)
			.then((total) => {
				if (!cancelled) {
					setNotesCount(total);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setNotesCount(null);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [expanded, organisationIdResolved, staffId]);

	if (!person || !staffId) {
		return null;
	}

	const countLabel =
		notesCount !== null && notesCount !== undefined
			? ` (${notesCount})`
			: "";

	return (
		<ExpansionPanel
			className={classes.root}
			elevation={0}
			expanded={expanded}
			onChange={onExpandedChange}
			data-cy="staff-photos-notes-panel"
		>
			<ExpansionPanelSummary
				expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
				className={classes.summary}
				aria-controls="staff-photos-notes-panel-content"
				id="staff-photos-notes-panel-header"
			>
				<Typography component="h2" className={classes.summaryTitle}>
					Photos / Notes
					{countLabel !== "" && (
						<span className={classes.countMuted}>{countLabel}</span>
					)}
				</Typography>
			</ExpansionPanelSummary>
			<ExpansionPanelDetails className={classes.details}>
				{expanded && onAddPhotoNote && (
					<div className={classes.addRow}>
						<Button
							className={classes.addButton}
							variant="contained"
							onClick={onAddPhotoNote}
							data-cy="staff-add-photo-note"
						>
							<AddIcon className={classes.addIcon} />
							Add Photos/Notes
						</Button>
					</div>
				)}
				{expanded && staffId && (
					<JobStaffList variant="embedded" staffId={staffId} />
				)}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

StaffPhotosNotesPanel.propTypes = {
	person: PropTypes.object,
	organisationId: PropTypes.string,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
	onAddPhotoNote: PropTypes.func,
};

export default StaffPhotosNotesPanel;
